import {
  Box,
  Stack,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Pagination,
  alpha,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material';
import { useState } from 'react';

import {
  useGetGroupPosts,
  useCreateGroupPost,
  useUpdatePost,
  useDeletePost,
  useTogglePostLike,
  useCreateReply,
  useToggleReplyLike,
  useDeleteReply,
  PostResponse,
} from '@/api/endpoints/community/posts';
import { PlusIcon } from '@/assets/icons/PlusIcon';

import { PostCard } from './PostCard';
import { PostForm, PostFormData } from './PostForm';
import { ReplyFormData } from './ReplyForm';

interface GroupPostsTabProps {
  groupId: number;
  isUserMember: boolean;
  isUserAdmin: boolean;
}

export const GroupPostsTab: React.FC<GroupPostsTabProps> = ({
  groupId,
  isUserMember,
  isUserAdmin,
}) => {
  const [currentPage, setCurrentPage] = useState(1);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingPost, setEditingPost] = useState<PostResponse | null>(null);

  const postsPerPage = 10;

  // API Hooks
  const {
    data: posts = [],
    isLoading,
    error,
  } = useGetGroupPosts({
    groupId,
    page: currentPage,
    limit: postsPerPage,
  });

  const createPostMutation = useCreateGroupPost();
  const updatePostMutation = useUpdatePost();
  const deletePostMutation = useDeletePost();
  const toggleLikeMutation = useTogglePostLike();
  const createReplyMutation = useCreateReply();
  const toggleReplyLikeMutation = useToggleReplyLike();
  const deleteReplyMutation = useDeleteReply();

  // Handlers
  const handleCreatePost = async (data: PostFormData) => {
    await createPostMutation.mutateAsync({
      groupId,
      ...data,
    });
    setShowCreateForm(false);
    setCurrentPage(1); // Go to first page to see new post
  };

  const handleUpdatePost = async (data: PostFormData) => {
    if (!editingPost) return;

    await updatePostMutation.mutateAsync({
      postId: editingPost.id,
      ...data,
    });
    setEditingPost(null);
  };

  const handleDeletePost = async (postId: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this post?')) {
      return;
    }

    await deletePostMutation.mutateAsync({
      postId,
      groupId,
    });
  };

  const handleToggleLike = async (postId: number) => {
    await toggleLikeMutation.mutateAsync({
      postId,
      groupId,
    });
  };

  const handleCreateReply = async (postId: number, data: ReplyFormData) => {
    await createReplyMutation.mutateAsync({
      postId,
      groupId,
      ...data,
    });
  };

  const handleToggleReplyLike = async (replyId: number, postId: number) => {
    await toggleReplyLikeMutation.mutateAsync({
      replyId,
      postId,
      groupId,
    });
  };

  const handleDeleteReply = async (replyId: number, postId: number) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm('Are you sure you want to delete this reply?')) {
      return;
    }

    await deleteReplyMutation.mutateAsync({
      replyId,
      postId,
      groupId,
    });
  };

  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleCancelCreate = () => {
    setShowCreateForm(false);
  };

  const handleCancelEdit = () => {
    setEditingPost(null);
  };

  const handleEditPost = (post: PostResponse) => {
    setEditingPost(post);
    setShowCreateForm(false); // Close create form if open
  };

  // Calculate total pages (this would come from API in real implementation)
  const totalPages = Math.max(1, Math.ceil(posts.length / postsPerPage));

  if (error) {
    return <Alert severity="error">Please join the group to participate.</Alert>;
  }

  return (
    <>
      <Stack spacing={3}>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: 'secondary.900' }}>
            Group Discussion
          </Typography>

          {isUserMember && (
            <Button
              startIcon={<PlusIcon />}
              variant="contained"
              onClick={() => setShowCreateForm(true)}
              sx={{
                bgcolor: 'primary.main',
                color: 'white',
                fontWeight: 500,
                px: 3,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                boxShadow: '0px 2px 8px rgba(157, 21, 0, 0.2)',
                '&:hover': {
                  bgcolor: 'primary.dark',
                  boxShadow: '0px 4px 12px rgba(157, 21, 0, 0.3)',
                },
              }}
            >
              Create Post
            </Button>
          )}
        </Box>

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Posts List */}
        {!isLoading && (
          <Stack spacing={3}>
            {posts.length === 0 ? (
              <Box
                sx={{
                  textAlign: 'center',
                  py: 6,
                  px: 3,
                  borderRadius: 2,
                  border: '2px dashed',
                  borderColor: 'secondary.200',
                  bgcolor: alpha('#f5f5f5', 0.5),
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 500, color: 'secondary.600', mb: 1 }}>
                  No posts yet
                </Typography>
                <Typography variant="body2" color="secondary.500" sx={{ mb: 3 }}>
                  {isUserMember
                    ? 'Be the first to start a discussion in this group!'
                    : 'Join this group to view and participate in discussions.'}
                </Typography>
                {isUserMember && (
                  <Button
                    startIcon={<PlusIcon />}
                    variant="contained"
                    onClick={() => setShowCreateForm(true)}
                    sx={{
                      bgcolor: 'primary.main',
                      color: 'white',
                      fontWeight: 500,
                      px: 3,
                      py: 1.5,
                      borderRadius: 2,
                      textTransform: 'none',
                    }}
                  >
                    Create First Post
                  </Button>
                )}
              </Box>
            ) : (
              <>
                {posts.map(post => (
                  <PostCard
                    key={post.id}
                    post={post}
                    onEdit={() => handleEditPost(post)}
                    onDelete={() => handleDeletePost(post.id)}
                    onToggleLike={() => handleToggleLike(post.id)}
                    onCreateReply={data => handleCreateReply(post.id, data)}
                    onToggleReplyLike={replyId => handleToggleReplyLike(replyId, post.id)}
                    onDeleteReply={replyId => handleDeleteReply(replyId, post.id)}
                    showActions={isUserMember}
                    isUserMember={isUserMember}
                    isUserAdmin={isUserAdmin}
                    createReplyLoading={createReplyMutation.isPending}
                  />
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      sx={{
                        '& .MuiPaginationItem-root': {
                          borderRadius: 2,
                        },
                      }}
                    />
                  </Box>
                )}
              </>
            )}
          </Stack>
        )}

        {/* Member-only message */}
        {!isUserMember && posts.length > 0 && (
          <Alert severity="info" sx={{ mt: 3 }}>
            Join this group to create posts, reply to discussions, and interact with other members.
          </Alert>
        )}
      </Stack>

      {/* Create Post Dialog */}
      <Dialog
        open={showCreateForm}
        onClose={handleCancelCreate}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            mx: { xs: 2, sm: 0 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            color: 'secondary.900',
            pb: 1,
          }}
        >
          Create New Post
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          <PostForm
            onSubmit={handleCreatePost}
            onCancel={handleCancelCreate}
            isLoading={createPostMutation.isPending}
            error={createPostMutation.error?.message}
            mode="create"
            showPinOption={isUserAdmin}
            title="Create New Post"
          />
        </DialogContent>
      </Dialog>

      {/* Edit Post Dialog */}
      <Dialog
        open={!!editingPost}
        onClose={handleCancelEdit}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            p: 1,
            mx: { xs: 2, sm: 0 },
          },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 600,
            fontSize: { xs: '1.25rem', sm: '1.5rem' },
            color: 'secondary.900',
            pb: 1,
          }}
        >
          Edit Post
        </DialogTitle>
        <DialogContent sx={{ pb: 2 }}>
          {editingPost && (
            <PostForm
              onSubmit={handleUpdatePost}
              onCancel={handleCancelEdit}
              initialData={editingPost}
              isLoading={updatePostMutation.isPending}
              error={updatePostMutation.error?.message}
              mode="edit"
              showPinOption={isUserAdmin}
              title="Edit Post"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
